/**
 * i18n — translations bundled directly into the JS chunk.
 *
 * WHY: loading translations via HTTP (/locales/xx/translation.json) caused
 * mobile browsers to serve stale cached files even after deploy, showing raw
 * i18n keys. By importing JSON files here, webpack bundles them into the
 * content-hashed JS bundle — translations can NEVER go stale.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import pt from './locales/pt/translation.json';
import es from './locales/es/translation.json';
import en from './locales/en/translation.json';
// Arabic — public-facing surfaces only. Missing keys fall back to English.
// Covers: common, menu, cart, checkout, orders, delivery, restaurant, publicMenu, errors.
// Dashboard / POS / admin keys are intentionally absent (fallbackLng: 'en' handles them).
import ar from './locales/ar/translation.json';

let storageWarningShown = false;
const safeStorage = {
  get(key) {
    try {
      return globalThis?.localStorage?.getItem(key) ?? null;
    } catch {
      if (!storageWarningShown) {
        storageWarningShown = true;
        console.warn("[i18n] localStorage unavailable, using runtime fallback locale");
      }
      return null;
    }
  },
  set(key, value) {
    try {
      globalThis?.localStorage?.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  remove(key) {
    try {
      globalThis?.localStorage?.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
};

const SUPPORTED = ['pt', 'es', 'en', 'ar'];

function normalizeCanonicalLocale(locale) {
  if (!locale) return 'es';
  const l = locale.toLowerCase();
  if (l.startsWith('pt')) return 'pt-BR';
  if (l.startsWith('es')) return 'es-ES';
  if (l.startsWith('en')) return 'en-GB';
  if (l.startsWith('ar')) return 'ar-SA';
  return 'es-ES';
}

function toBaseLanguage(canonical) {
  if (!canonical) return 'es';
  const base = canonical.split('-')[0].toLowerCase();
  return SUPPORTED.includes(base) ? base : 'es';
}

const getInitialLanguage = () => {
  // 1. Explicit user preference — must be checked before restaurant locale
  const saved = safeStorage.get('kaiso_language');
  if (saved) return toBaseLanguage(saved);

  const savedCanonical = safeStorage.get('kaiso_locale_canonical');
  if (savedCanonical) return toBaseLanguage(savedCanonical);

  // 2. Restaurant locale (set when public menu loads — never overrides user pref)
  const restaurantData = safeStorage.get('kaiso_restaurant_locale');
  if (restaurantData) {
    try {
      const { locale } = JSON.parse(restaurantData);
      const canonical = normalizeCanonicalLocale(locale);
      safeStorage.set('kaiso_restaurant_locale', JSON.stringify({ locale: canonical }));
      return toBaseLanguage(canonical);
    } catch {}
  }

  const htmlLang = typeof document !== 'undefined' ? document.documentElement?.lang : '';
  const navigatorLang = typeof navigator !== 'undefined' ? navigator.language : '';
  const canonicalBrowser = normalizeCanonicalLocale(navigatorLang || htmlLang || (typeof navigator !== 'undefined' ? navigator.language : ''));
  return toBaseLanguage(canonicalBrowser);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pt: { translation: pt },
      es: { translation: es },
      en: { translation: en },
      ar: { translation: ar },
    },

    fallbackLng: 'en',
    supportedLngs: ['pt', 'es', 'en', 'ar'],
    nonExplicitSupportedLngs: true,

    lng: getInitialLanguage(),

    debug: process.env.NODE_ENV === 'development',

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'kaiso_language',
      caches: [],
    },

    react: {
      useSuspense: false,
    },
  });

// Apply text direction to <html> element for RTL languages (Arabic).
// Also sets html[lang] so CSS :lang() selectors and screen readers work correctly.
const applyDocumentDirection = (lang) => {
  if (typeof document === 'undefined') return;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
};

// Apply direction based on initial language detected at boot
applyDocumentDirection(getInitialLanguage());

// Helper function to change language and persist
export const changeLanguage = (lang) => {
  const canonical = normalizeCanonicalLocale(lang);
  const baseLang = toBaseLanguage(canonical);
  safeStorage.set('kaiso_language', baseLang);
  safeStorage.set('kaiso_locale_canonical', canonical);
  i18n.changeLanguage(baseLang);
  applyDocumentDirection(baseLang);
};

// Activate Arabic for a public menu session.
// Called ONLY when restaurant.arabic_enabled = true AND restaurant.ui_language = "ar".
// This is an explicit restaurant decision — not a user preference or browser heuristic.
// Does NOT write to kaiso_language or kaiso_locale_canonical (user/dashboard keys).
// Uses kaiso_restaurant_locale (restaurant-scoped key) to record the visit.
export const setArabicMenuLocale = () => {
  safeStorage.set('kaiso_restaurant_locale', JSON.stringify({ locale: 'ar-SA' }));
  i18n.changeLanguage('ar');
};

// Helper to set language based on restaurant locale (called by public menu).
// MUST NOT write kaiso_language or kaiso_locale_canonical — those belong to the
// authenticated user's explicit preference and must not be contaminated by a
// public menu visit.
export const setLanguageFromLocale = (locale) => {
  if (!locale) return;
  const canonical = normalizeCanonicalLocale(locale);
  const lang = toBaseLanguage(canonical);
  safeStorage.set('kaiso_restaurant_locale', JSON.stringify({ locale: canonical }));
  // Only switch UI language if the user has no explicit preference at all
  if (!safeStorage.get('kaiso_language') && !safeStorage.get('kaiso_locale_canonical')) {
    i18n.changeLanguage(lang);
  }
};

export { toBaseLanguage };

// Get available languages
// Arabic is included but hidden from the language-switcher UI by default —
// activation is via restaurant.arabic_enabled, not user selection.
export const availableLanguages = [
  { code: 'pt', name: 'Portugues', nativeName: 'Portugues', flag: '🇧🇷' },
  { code: 'es', name: 'Espanol', nativeName: 'Espanol', flag: '🇪🇸' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', hidden: true },
];

export default i18n;
